import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Fullscreen modal for playing an embedded video (Rutube/VK iframe).
 * Expects: video = { label, url, embedUrl }
 */
const VideoModal = ({ isOpen, onClose, video }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const title = video?.label || 'Видео';
  const url = video?.url || null;
  const embedUrl = video?.embedUrl || null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        // click outside to close
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
        aria-label="Закрыть"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <div className="w-full max-w-5xl">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white/5 border-b border-white/10">
            <div className="text-sm font-medium text-white truncate">{title}</div>
            {url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/80 hover:text-white underline"
              >
                Открыть в новой вкладке
              </a>
            ) : null}
          </div>

          {embedUrl ? (
            <div className="relative w-full pt-[56.25%] bg-black">
              {/*
                key={embedUrl} forces iframe remount when switching videos,
                so previous playback is stopped.
              */}
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="p-4 text-sm text-white/80">
              Не удалось встроить видео. {url ? 'Откройте ссылку выше.' : null}
            </div>
          )}
        </div>

        <div className="mt-3 text-right">
          <span className="inline-block text-xs text-white/70 bg-white/10 px-3 py-1 rounded-full">ESC для закрытия</span>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;

